import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { authenticatedFetch } from '../services/authService';
import { socketService } from '@platform/socket';
import type { ChatConversation } from '@ui/TopBar';

interface ChatContextType {
  conversations: ChatConversation[];
  unreadCount: number;
  loading: boolean;
  refreshConversations: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refreshConversations = useCallback(async () => {
    if (!user) {
      setConversations([]);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/chat/conversations', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConversations(data.conversations || []);
        }
      }
    } catch (err) {
      console.error('[ChatContext] Error loading conversations:', err);
    }
  }, [user]);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/chat/unread-count', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUnreadCount(data.unreadCount || 0);
        }
      }
    } catch (err) {
      console.error('[ChatContext] Error loading unread count:', err);
    }
  }, [user]);

  // Load data on mount and when user changes
  useEffect(() => {
    if (user) {
      setLoading(true);
      Promise.all([refreshConversations(), refreshUnreadCount()]).finally(() => {
        setLoading(false);
      });
    } else {
      setConversations([]);
      setUnreadCount(0);
    }
  }, [user, refreshConversations, refreshUnreadCount]);

  // Socket.IO realtime updates
  useEffect(() => {
    if (!user) return;

    const handleDbChange = (payload: any) => {
      try {
        if (payload.table === 'chat_message') {
          const msg = payload.row;
          if (!msg) return;

          // Check if this message is relevant to current user
          const isRelevant = msg.sender_id === user.id || msg.receiver_id === user.id;

          if (isRelevant) {
            // Refresh conversations and unread count on any chat message change
            refreshConversations();
            refreshUnreadCount();
          }
        }
      } catch (err) {
        console.error('[ChatContext] Error processing socket event:', err);
      }
    };

    const unsubscribe = socketService.subscribe(handleDbChange);

    return () => {
      unsubscribe();
    };
  }, [user, refreshConversations, refreshUnreadCount]);

  // Periodic refresh (every 30 seconds) to catch any missed updates
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshConversations();
      refreshUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, refreshConversations, refreshUnreadCount]);

  return (
    <ChatContext.Provider
      value={{
        conversations,
        unreadCount,
        loading,
        refreshConversations,
        refreshUnreadCount
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

